import BoardStrip from "@/components/landing/BoardStrip";
import { formatCount, useCoverage } from "@/lib/coverage";

/**
 * How much of the job market NextOpp reads, under the hero.
 *
 * Scale is the first question anyone asks of a job tool — "are the jobs I
 * want in here?" — so it is answered before the feature tour. Every figure is
 * fetched from the live index and rounded down; none is typed here. When the
 * fetch fails the band shows the ATS names alone, because a page that says
 * "Nothing invented" cannot show a stale number while the real one is
 * unavailable.
 *
 * On the dark hero the figures use the white ramp — the Daybreak rule for
 * text on night; `stone` here lands near 2.2:1.
 */
const FIGURES = [
  { key: "open_roles", label: "open roles" },
  { key: "companies_watched", label: "companies watched" },
  { key: "countries", label: "countries" },
  { key: "ats_systems", label: "ATS systems read" },
];

export default function CoverageBand({ dark = false }) {
  const coverage = useCoverage();
  const figures = coverage
    ? FIGURES.map((f) => ({ ...f, value: formatCount(coverage[f.key]) })).filter((f) => f.value)
    : [];

  const num = dark ? "text-white" : "text-stone-950";
  const label = dark ? "text-white/55" : "text-stone-600";
  const note = dark ? "text-white/40" : "text-stone-500";
  const rule = dark ? "border-white/10" : "border-sand";

  return (
    <div data-testid="coverage-band">
      {figures.length > 0 && (
        <div data-testid="coverage-figures">
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-x-8 gap-y-5">
            {figures.map((f) => (
              <div key={f.key}>
                <dd className={`font-heading text-3xl lg:text-4xl font-bold tracking-tight tabular-nums ${num}`}>
                  {f.value}
                </dd>
                <dt className={`mt-1 text-[11px] uppercase tracking-[0.18em] font-bold ${label}`}>
                  {f.label}
                </dt>
              </div>
            ))}
          </dl>
          <p className={`mt-3 text-xs ${note}`}>
            Counted from the live index, not typed in. Rounded down, so the real number is higher.
          </p>
        </div>
      )}
      <div className={figures.length > 0 ? `mt-6 pt-5 border-t ${rule}` : ""}>
        <BoardStrip dark={dark} />
      </div>
    </div>
  );
}
